import * as React from 'react';
import * as _ from 'lodash';
import {
    AudienceRule,
    AudienceRuleOption,
    Question,
    QuestionSummary
} from '@nexxt/common/types';
import { errorObject } from '../../AudienceRule';
import AudienceRuleHeader from '../AudienceRuleHeader';
import NumericAudienceRuleOption from './NumericAudienceRuleOption';

interface NumericAudienceRuleProps {
    audienceRule: AudienceRule;
    isMenuOpen: boolean;
    isDisabled: boolean;
    isBuiltIn?: boolean;
    totalSampleSize: number;
    hasInvalid: boolean;
    errorObject: errorObject;
    health?: any;
    numericOptions: {
        rangeOptions: AudienceRuleOption[];
        beforeRangeOption: AudienceRuleOption;
        afterRangeOption: AudienceRuleOption;
        notApplicableOption: AudienceRuleOption;
    };
    selectedQuestionSummary: QuestionSummary | Question;
    setIsMenuOpen: () => void;
    onAddNewOption: (quotaEnabled: boolean) => void;
    onEdit: (editingQuestion) => void;
    onDelete: () => void;
    disableByFieldName: (keyName?: string) => boolean;
    onEditOptions: (numericOptions: unknown, toggle: boolean) => void;
    onDeleteOption: (index: number, quotaEnabled: boolean) => void;
}
enum NUMERIC_OPTION_INDEX {
    BEFORE_RANGE = -1,
    AFTER_RANGE = -2,
    NOT_APPLICABLE = -3
}
const NumericAudienceRule = ({
    audienceRule,
    isDisabled,
    isMenuOpen,
    isBuiltIn,
    hasInvalid,
    errorObject,
    health,
    totalSampleSize,
    numericOptions,
    selectedQuestionSummary,
    setIsMenuOpen,
    onAddNewOption,
    onEdit,
    onDelete,
    disableByFieldName,
    onEditOptions,
    onDeleteOption
}: NumericAudienceRuleProps): JSX.Element => {
    const [quotaEnabled, setQuotaEnabled] = React.useState<boolean>(false);

    // Set initial toggle value for quotaEnabled
    React.useEffect(() => {
        if (audienceRule?.audienceRuleOptions?.length) {
            setQuotaEnabled(
                audienceRule.audienceRuleOptions.some(
                    (opt) => opt.mode?.quotaEnabled
                )
            );
        }
    }, []);

    const hasHalfWidth = React.useMemo<boolean>(
        () => audienceRule.audienceRuleOptions.length < 6,
        []
    );

    const updateNumericOptionByIndex = (
        index: number,
        newValue: { keyName: string; value: unknown }
    ): void => {
        const { keyName, value } = newValue;
        const _numericOptions = _.cloneDeep(numericOptions);
        switch (true) {
            // beforeRangeOption
            case index === NUMERIC_OPTION_INDEX.BEFORE_RANGE:
                _numericOptions.beforeRangeOption = {
                    ..._numericOptions.beforeRangeOption,
                    [keyName]: value
                };
                break;
            // afterRangeOption
            case index === NUMERIC_OPTION_INDEX.AFTER_RANGE:
                _numericOptions.afterRangeOption = {
                    ..._numericOptions.afterRangeOption,
                    [keyName]: value
                };
                break;
            // NA option
            case index === NUMERIC_OPTION_INDEX.NOT_APPLICABLE:
                _numericOptions.notApplicableOption = {
                    ..._numericOptions.notApplicableOption,
                    [keyName]: value
                };
                break;
            // Regular range options
            default:
                const currentNumericRangeOption =
                    _numericOptions.rangeOptions[index];
                _numericOptions.rangeOptions = [
                    ..._numericOptions.rangeOptions.slice(0, index),
                    {
                        ...currentNumericRangeOption,
                        [keyName]: value
                    },
                    ..._numericOptions.rangeOptions.slice(index + 1)
                ];
                break;
        }
        onEditOptions(_numericOptions, quotaEnabled);
    };

    const onChangeFilterToggle = (quotaEnabled: boolean): void => {
        setQuotaEnabled(quotaEnabled);

        const _numericOptions = _.cloneDeep(numericOptions);
        if (_numericOptions.beforeRangeOption) {
            _numericOptions.beforeRangeOption.mode = {
                ..._numericOptions.beforeRangeOption.mode,
                quotaEnabled
            };
        }
        if (_numericOptions.afterRangeOption) {
            _numericOptions.afterRangeOption.mode = {
                ..._numericOptions.afterRangeOption.mode,
                quotaEnabled
            };
        }
        if (_numericOptions.notApplicableOption) {
            _numericOptions.notApplicableOption.mode = {
                ..._numericOptions.notApplicableOption.mode,
                quotaEnabled
            };
        }
        if (_numericOptions.rangeOptions?.length) {
            _numericOptions.rangeOptions.forEach((rangeOption) => {
                rangeOption.mode = { ...rangeOption.mode, quotaEnabled };
            });
        }

        onEditOptions(_numericOptions, quotaEnabled);
    };

    const calculateSampleSizeInUse = (): number => {
        let total = 0;
        if (quotaEnabled) {
            total = [
                numericOptions.afterRangeOption,
                numericOptions.beforeRangeOption,
                numericOptions.notApplicableOption,
                ...numericOptions.rangeOptions
            ].reduce((total, option) => {
                if (!option?.mode?.terminateEnabled) {
                    return total + (option?.sampleSize?.max || 0);
                }
                return total;
            }, 0);
        }
        return total;
    };

    const onChangeTargetingOption = (
        index: number,
        mode: { terminateEnabled: boolean; quotaEnabled: boolean }
    ): void => {
        updateNumericOptionByIndex(index, { keyName: 'mode', value: mode });
    };

    const onSampleSizeChange = (index: number, value: number): void => {
        updateNumericOptionByIndex(index, {
            keyName: 'sampleSize',
            value: {
                min: value,
                max: value
            }
        });
    };

    const renderAudienceRules = (): JSX.Element => {
        const totalSampleInUse = calculateSampleSizeInUse();
        const isQuotaTotalError = totalSampleSize < totalSampleInUse;

        const selectedQuestionId = audienceRule.selectedQuestion.questionId;
        if (
            !numericOptions.rangeOptions.length &&
            !numericOptions.notApplicableOption
        ) {
            return null;
        }
        return (
            <table>
                <colgroup className="column-4">
                    <col span={1} />
                    <col span={1} />
                    <col span={1} />
                    {quotaEnabled && <col span={1} />}
                </colgroup>
                <tbody>
                    <tr className="content-header">
                        <td>From</td>
                        <td>To</td>
                        <td align="left" style={{ whiteSpace: 'nowrap' }}>
                            {isBuiltIn
                                ? 'Targeting Options'
                                : 'Screening Logic'}
                        </td>
                        {quotaEnabled && (
                            <td
                                align="right"
                                className={`${
                                    isQuotaTotalError && 'error-text'
                                }`}
                            >
                                Quota({totalSampleInUse}/{totalSampleSize})
                            </td>
                        )}
                    </tr>
                    {numericOptions.beforeRangeOption ? (
                        <NumericAudienceRuleOption
                            option={numericOptions.beforeRangeOption}
                            index={NUMERIC_OPTION_INDEX.BEFORE_RANGE}
                            errorObject={{}}
                            isDisabled={isDisabled}
                            quotaEnabled={quotaEnabled}
                            onEditOptions={updateNumericOptionByIndex}
                            onChangeTargetingOption={onChangeTargetingOption}
                            onSampleSizeChange={onSampleSizeChange}
                            systemGenerated
                        />
                    ) : null}
                    {numericOptions.rangeOptions.map((opt, ind) => (
                        <NumericAudienceRuleOption
                            key={`${selectedQuestionId}-option-${ind}`}
                            option={opt}
                            index={ind}
                            errorObject={errorObject?.ageRange?.[ind]}
                            isDisabled={isDisabled}
                            quotaEnabled={quotaEnabled}
                            onEditOptions={updateNumericOptionByIndex}
                            onChangeTargetingOption={onChangeTargetingOption}
                            onDeleteOption={(index) =>
                                onDeleteOption(index, quotaEnabled)
                            }
                            onSampleSizeChange={onSampleSizeChange}
                        />
                    ))}
                    {numericOptions.afterRangeOption ? (
                        <NumericAudienceRuleOption
                            option={numericOptions.afterRangeOption}
                            index={NUMERIC_OPTION_INDEX.AFTER_RANGE}
                            errorObject={{}}
                            isDisabled={isDisabled}
                            quotaEnabled={quotaEnabled}
                            onEditOptions={updateNumericOptionByIndex}
                            onChangeTargetingOption={onChangeTargetingOption}
                            onSampleSizeChange={onSampleSizeChange}
                            systemGenerated
                        />
                    ) : null}
                    {numericOptions.notApplicableOption ? (
                        <NumericAudienceRuleOption
                            option={numericOptions.notApplicableOption}
                            index={NUMERIC_OPTION_INDEX.NOT_APPLICABLE}
                            errorObject={{}}
                            isDisabled={isDisabled}
                            quotaEnabled={quotaEnabled}
                            onEditOptions={updateNumericOptionByIndex}
                            onChangeTargetingOption={onChangeTargetingOption}
                            onSampleSizeChange={onSampleSizeChange}
                            systemGenerated
                        />
                    ) : null}
                </tbody>
            </table>
        );
    };
    return (
        <>
            <AudienceRuleHeader
                audienceRule={audienceRule}
                toggle={quotaEnabled}
                quotaEnabled
                isDisabled={isDisabled}
                isMenuOpen={isMenuOpen}
                isBuiltIn={isBuiltIn}
                isHalfWidth={hasHalfWidth}
                hasInvalid={hasInvalid}
                errorObject={errorObject}
                setIsMenuOpen={setIsMenuOpen}
                onEdit={onEdit}
                onDelete={onDelete}
                onChangeFilterToggle={onChangeFilterToggle}
                disableByFieldName={disableByFieldName}
                health={health}
            />

            {renderAudienceRules()}
            {!isDisabled && (
                <span
                    className="outside-link"
                    onClick={() => {
                        onAddNewOption(quotaEnabled);
                    }}
                >
                    + Add a new age range
                </span>
            )}
        </>
    );
};

export default NumericAudienceRule;
